/**
 * Execution Store (Zustand)
 *
 * Owns pipeline execution state only. No editor state.
 */

import { create } from 'zustand';

import { pipelineEngine } from '@/engine/PipelineEngine';
import type { IPipelineStep, IStepResult } from '@/interfaces/IPipeline';
import type { ISemanticType } from '@/interfaces/ISemanticType';

export interface StepThroughState {
  active: boolean;
  cursor: number; // 0-based index into pipeline
  currentData: string;
  currentType: ISemanticType | null;
  failedStepId: string | null;
}

/** Snapshot of I/O state captured when debug starts, restored on stop. */
interface DebugSnapshot {
  input: string;
  output: string;
  stepResults: IStepResult[];
  inputType: ISemanticType | null;
  errors: Record<string, string>;
}

export interface ExecutionContext {
  input: string;
  output?: string;
  inputType: ISemanticType | null;
  pipelineName?: string;
  variables: Record<string, string>;
  steps: IPipelineStep[];
  isStale?: () => boolean;
}

export interface StepThroughContext {
  pipelineName?: string;
  variables: Record<string, string>;
  steps: IPipelineStep[];
  isStale?: () => boolean;
}

export interface ExecutionResult {
  output: string;
  stepResults: IStepResult[];
  outputType: ISemanticType | null;
}

export interface StepThroughResult {
  nextInput: string;
  nextOutput: string;
  nextOutputType: ISemanticType | null;
}

interface ExecutionState {
  isProcessing: boolean;
  error: string | null;
  errors: Record<string, string>;
  stepResults: IStepResult[];
  currentStepIndex: number | null;
  stepThrough: StepThroughState | null;
  debugSnapshot: DebugSnapshot | null;
  isStepThrough: boolean;
  executionRevision: number;
  rerunFromStepIndex: number | null;
  suppressAutoRun: boolean;

  runPipeline: (context: ExecutionContext) => Promise<ExecutionResult | null>;
  cancelPipeline: () => void;
  rerunFromStep: (context: ExecutionContext, stepIndex: number, newInput: string, priorResults: IStepResult[]) => Promise<ExecutionResult | null>;
  startStepThrough: (context: ExecutionContext) => void;
  stepThroughNext: (context: StepThroughContext) => Promise<StepThroughResult | null>;
  stopStepThrough: () => DebugSnapshot | null;
  resetExecution: (options?: { clearStepThrough?: boolean }) => void;
  suppressNextAutoRun: () => void;
  consumeAutoRunSuppression: () => boolean;
}

function lastOutputType(stepResults: IStepResult[]): ISemanticType | null {
  if (stepResults.length === 0) return null;
  return stepResults[stepResults.length - 1]?.outputType ?? null;
}

export const useExecutionStore = create<ExecutionState>((set, get) => ({
  isProcessing: false,
  error: null,
  errors: {},
  stepResults: [],
  currentStepIndex: null,
  stepThrough: null,
  debugSnapshot: null,
  isStepThrough: false,
  executionRevision: 0,
  rerunFromStepIndex: null,
  suppressAutoRun: false,

  resetExecution: (options) => {
    const clearStepThrough = options?.clearStepThrough ?? true;
    set((state) => ({
      isProcessing: false,
      error: null,
      errors: {},
      stepResults: [],
      currentStepIndex: null,
      rerunFromStepIndex: null,
      suppressAutoRun: false,
      executionRevision: state.executionRevision + 1,
      ...(clearStepThrough && { stepThrough: null, debugSnapshot: null, isStepThrough: false }),
    }));
  },

  cancelPipeline: () => {
    set((state) => ({
      isProcessing: false,
      suppressAutoRun: false,
      executionRevision: state.executionRevision + 1,
    }));
  },

  runPipeline: async (context) => {
    const state = get();
    if (state.isProcessing) return null;
    if (state.stepThrough?.active) return null;
    if (context.steps.length === 0) return null;

    const runRevision = state.executionRevision;
    const isStale = () => get().executionRevision !== runRevision || context.isStale?.() === true;

    set({
      isProcessing: true,
      error: null,
      errors: {},
      stepResults: [],
      currentStepIndex: null,
      rerunFromStepIndex: null,
    });

    try {
      const result = await pipelineEngine.execute({
        input: context.input,
        inputType: context.inputType || undefined,
        pipelineName: context.pipelineName,
        variables: context.variables,
        steps: context.steps,
        onStepComplete: (stepId, stepResult) => {
          if (isStale()) return;
          set((s) => ({
            stepResults: [...s.stepResults, stepResult],
            currentStepIndex: context.steps.findIndex((step) => step.id === stepId),
          }));
        },
        onStepError: (stepId, error) => {
          if (isStale()) return;
          set((s) => {
            const nextErrors = { ...s.errors, [stepId]: error.message };
            return {
              errors: nextErrors,
              error: nextErrors._pipeline ?? null,
            };
          });
        },
      });

      if (isStale()) return null;

      set({
        isProcessing: false,
        stepResults: result.stepResults,
        currentStepIndex: context.steps.length - 1,
      });

      return {
        output: result.output,
        stepResults: result.stepResults,
        outputType: lastOutputType(result.stepResults),
      };
    } catch (error) {
      if (isStale()) return null;
      set({
        isProcessing: false,
        errors: { _pipeline: error instanceof Error ? error.message : 'Unknown error' },
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return null;
    }
  },

  rerunFromStep: async (context, stepIndex, newInput, priorResults) => {
    const state = get();
    if (state.isProcessing) return null;

    const runRevision = state.executionRevision;
    const isStale = () => get().executionRevision !== runRevision || context.isStale?.() === true;

    set({
      isProcessing: true,
      error: null,
      errors: {},
      currentStepIndex: null,
      rerunFromStepIndex: stepIndex,
    });

    try {
      const result = await pipelineEngine.executeFrom(
        {
          input: context.input,
          inputType: context.inputType ?? undefined,
          pipelineName: context.pipelineName,
          variables: context.variables,
          steps: context.steps,
        },
        stepIndex,
        newInput,
        priorResults,
      );

      if (isStale()) return null;

      const newErrors: Record<string, string> = {};
      for (const sr of result.stepResults) {
        if (sr.status === 'error' && sr.error) {
          newErrors[sr.stepId] = sr.error.message;
        }
      }

      set({
        isProcessing: false,
        errors: newErrors,
        error: newErrors._pipeline ?? null,
        stepResults: result.stepResults,
        currentStepIndex: result.stepResults.length - 1,
      });

      return {
        output: result.output,
        stepResults: result.stepResults,
        outputType: lastOutputType(result.stepResults),
      };
    } catch (error) {
      if (isStale()) return null;
      set({
        isProcessing: false,
        errors: { _pipeline: error instanceof Error ? error.message : 'Unknown error' },
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return null;
    }
  },

  startStepThrough: (context) => {
    const state = get();
    if (state.isProcessing) return;
    if (context.steps.length === 0) return;

    set({
      debugSnapshot: {
        input: context.input,
        output: context.output ?? '',
        stepResults: state.stepResults,
        inputType: context.inputType,
        errors: state.errors,
      },
      stepThrough: {
        active: true,
        cursor: 0,
        currentData: context.input,
        currentType: context.inputType,
        failedStepId: null,
      },
      isStepThrough: true,
      isProcessing: false,
      error: null,
      errors: {},
      stepResults: [],
      currentStepIndex: 0,
      rerunFromStepIndex: null,
    });
  },

  stepThroughNext: async (context) => {
    const state = get();
    const session = state.stepThrough;

    if (!session?.active) return null;
    if (state.isProcessing) return null;
    if (session.failedStepId) return null;
    if (session.cursor >= context.steps.length) return null;

    const runRevision = state.executionRevision;
    const isStale = () => get().executionRevision !== runRevision || context.isStale?.() === true;

    const step = context.steps[session.cursor];

    set({ isProcessing: true, currentStepIndex: session.cursor });

    try {
      const result = await pipelineEngine.execute({
        input: session.currentData,
        inputType: session.currentType || undefined,
        pipelineName: context.pipelineName,
        variables: context.variables,
        steps: [step],
      });

      if (isStale()) return null;

      const stepResult = result.stepResults[0];

      const nextInput = stepResult?.input ?? session.currentData;
      const nextOutput = stepResult?.output ?? '';
      const nextOutputType = stepResult?.outputType ?? session.currentType;

      const nextStepResults = [...state.stepResults, stepResult];

      const nextErrors: Record<string, string> = { ...state.errors };
      if (stepResult.status === 'error') {
        nextErrors[step.id] = stepResult.error?.message ?? 'Unknown error';
      }

      set({
        isProcessing: false,
        errors: nextErrors,
        error: nextErrors._pipeline ?? null,
        stepResults: nextStepResults,
        currentStepIndex: session.cursor,
        stepThrough: {
          active: true,
          cursor: session.cursor + 1,
          currentData: stepResult.status === 'error' ? session.currentData : nextOutput,
          currentType: stepResult.status === 'error' ? session.currentType : (nextOutputType as ISemanticType | null),
          failedStepId: stepResult.status === 'error' ? step.id : null,
        },
      });

      return {
        nextInput,
        nextOutput,
        nextOutputType: nextOutputType as ISemanticType | null,
      };
    } catch (error) {
      if (isStale()) return null;
      set((s) => ({
        isProcessing: false,
        errors: { ...s.errors, _pipeline: error instanceof Error ? error.message : 'Unknown error' },
        error: error instanceof Error ? error.message : 'Unknown error',
        stepThrough: s.stepThrough
          ? { ...s.stepThrough, failedStepId: context.steps[s.stepThrough.cursor]?.id ?? null }
          : null,
      }));
      return null;
    }
  },

  stopStepThrough: () => {
    const state = get();
    const snapshot = state.debugSnapshot;
    if (!state.stepThrough && !snapshot) return null;

    set((s) => ({
      stepThrough: null,
      debugSnapshot: null,
      isStepThrough: false,
      isProcessing: false,
      currentStepIndex: null,
      executionRevision: s.executionRevision + 1,
      ...(snapshot && {
        stepResults: snapshot.stepResults,
        errors: snapshot.errors,
        error: snapshot.errors._pipeline ?? null,
      }),
    }));

    return snapshot;
  },

  suppressNextAutoRun: () => {
    set({ suppressAutoRun: true });
  },

  consumeAutoRunSuppression: () => {
    const shouldSuppress = get().suppressAutoRun;
    if (shouldSuppress) {
      set({ suppressAutoRun: false });
    }
    return shouldSuppress;
  },
}));
